import React, { useEffect, useRef, useState } from 'react';

type Status = 'arrived' | 'departed';
type Mood = 'great' | 'okay' | 'not_good' | 'skip';
type Step = 'choose' | 'form' | 'confirmed';


const GUEST_NAME_COOKIE = 'corazon_guest_name';
const GUEST_NAME_MAX_LENGTH = 80;
const GUEST_EMAIL_MAX_LENGTH = 254;
const GUEST_NAME_COOKIE_MAX_AGE = 60 * 60 * 24 * 30;
const STATUS_DEVICE_COOKIE = 'corazon_status_device';
const STATUS_DEVICE_COOKIE_MAX_AGE = 60 * 60 * 24 * 90;
const STATUS_DEVICE_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const readCookieValue = (name: string) => {
  const prefix = `${name}=`;
  const entry = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));

  if (!entry) return '';
  try {
    return decodeURIComponent(entry.slice(prefix.length));
  } catch {
    return '';
  }
};

const saveStatusDeviceCookie = (deviceId: string) => {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${STATUS_DEVICE_COOKIE}=${encodeURIComponent(deviceId)}; Max-Age=${STATUS_DEVICE_COOKIE_MAX_AGE}; Path=/; SameSite=Lax${secure}`;
};

const ensureStatusDeviceCookie = () => {
  const existing = readCookieValue(STATUS_DEVICE_COOKIE);
  if (STATUS_DEVICE_ID_PATTERN.test(existing)) return existing;

  const deviceId = crypto.randomUUID();
  saveStatusDeviceCookie(deviceId);
  return deviceId;
};

const normalizeGuestName = (value: string) =>
  value.trim().replace(/\s+/g, ' ').slice(0, GUEST_NAME_MAX_LENGTH) || 'Guest';

const readGuestNameCookie = () => {
  const cookiePrefix = `${GUEST_NAME_COOKIE}=`;
  const cookie = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(cookiePrefix));

  if (!cookie) return 'Guest';

  try {
    return normalizeGuestName(decodeURIComponent(cookie.slice(cookiePrefix.length)));
  } catch {
    return 'Guest';
  }
};

const saveGuestNameCookie = (guestName: string) => {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${GUEST_NAME_COOKIE}=${encodeURIComponent(guestName)}; Max-Age=${GUEST_NAME_COOKIE_MAX_AGE}; Path=/; SameSite=Lax${secure}`;
};

const CHECK_IN_MOOD_OPTIONS: { value: Mood; label: string }[] = [
  { value: 'great', label: 'Great' },
  { value: 'okay', label: 'Okay' },
  { value: 'not_good', label: 'Not good' },
  { value: 'skip', label: 'Skip' },
];

// Small drawn faces for the mood picker (mouth path per mood; skip is a dashed circle).
const MOUTHS: Record<Mood, string> = {
  great: 'M9.5 17.5q5.5 5 11 0',
  okay: 'M10 19h10',
  not_good: 'M9.5 21q5.5-5 11 0',
  skip: 'M10 15h10',
};
const MoodFace: React.FC<{ mood: Mood }> = ({ mood }) => (
  <svg viewBox="0 0 30 30" width="30" height="30" aria-hidden="true">
    <circle cx="15" cy="15" r="12" fill={mood === 'skip' ? 'none' : 'var(--st-paper)'} stroke="currentColor" strokeWidth="2.2" strokeDasharray={mood === 'skip' ? '4 3' : undefined} />
    {mood !== 'skip' && <><circle cx="11" cy="12" r="1.6" fill="currentColor" /><circle cx="19" cy="12" r="1.6" fill="currentColor" /></>}
    <path d={MOUTHS[mood]} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);

const KeyIcon = () => (
  <svg viewBox="0 0 28 28" aria-hidden="true">
    <circle cx="9" cy="14" r="6" fill="#FFF7E6" stroke="#11172F" strokeWidth="2" />
    <path d="M15 14h11M22 14v5M26 14v4" stroke="#11172F" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
const SuitcaseIcon = () => (
  <svg viewBox="0 0 28 28" aria-hidden="true">
    <rect x="4" y="9" width="20" height="15" rx="3" fill="#E4573D" stroke="#11172F" strokeWidth="2" />
    <path d="M10 9V5h8v4M4 15h20" fill="none" stroke="#11172F" strokeWidth="2" strokeLinejoin="round" />
  </svg>
);

// Drawn Space Needle and a few buildings on a green hill, along the bottom of the choose screen.
const Skyline = () => (
  <svg className="status-skyline" viewBox="0 0 375 210" preserveAspectRatio="xMidYMax slice" aria-hidden="true">
    <path d="M0 150q90-40 190-8t185-14V210H0Z" fill="#3E8E8A" stroke="#11172F" strokeWidth="2.5" />
    <path d="M248 160V70M240 70h16l-2-8h-12zM232 62q16-14 32 0zM248 48V38" fill="#FFF7E6" stroke="#11172F" strokeWidth="2.5" strokeLinejoin="round" />
    <rect x="60" y="118" width="26" height="50" fill="#263A72" stroke="#11172F" strokeWidth="2.5" />
    <rect x="90" y="96" width="30" height="72" fill="#F3C35B" stroke="#11172F" strokeWidth="2.5" />
    <rect x="124" y="126" width="24" height="42" fill="#E4573D" stroke="#11172F" strokeWidth="2.5" />
    <rect x="290" y="124" width="28" height="44" fill="#263A72" stroke="#11172F" strokeWidth="2.5" />
    <path d="M0 180q90-20 190 0t185-6V210H0Z" fill="#263A72" />
  </svg>
);

async function postGuestStatus(body: Record<string, unknown>) {
  const response = await fetch('/api/guest-status', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    keepalive: true,
  });

  if (!response.ok) {
    throw new Error(`Guest status notification failed with ${response.status}`);
  }
}

const formatDate = (date: Date) => date.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
const formatTime = (date: Date) => date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
const formatPostmark = (date: Date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase();

const FORM_COPY: Record<Status, { eyebrow: string; title: React.ReactNode; moodLegend: string; submit: string; pin: string }> = {
  arrived: {
    eyebrow: 'Check-in',
    title: <>Welcome to <em>Corazón Townhouse.</em></>,
    moodLegend: 'How did arrival go?',
    submit: 'Check In',
    pin: 'Checking in · Capitol Hill',
  },
  departed: {
    eyebrow: 'Check-out',
    title: <>Thanks for <em>staying with us.</em></>,
    moodLegend: 'How was your stay?',
    submit: 'Check Out',
    pin: 'Checking out · Capitol Hill',
  },
};

type OfferState = 'idle' | 'sending' | 'sent' | 'already' | 'error';

// "Coming back?" card on the done screen: the guest can ask for a one-time
// email with 15% off their next stay. /api/next-stay-offer sends it once per
// email address, so asking at check-in and again at check-out is harmless.
const NextStayOffer: React.FC<{ status: Status; name: string; initialEmail: string }> = ({ status, name, initialEmail }) => {
  const [email, setEmail] = useState(initialEmail);
  const [state, setState] = useState<OfferState>('idle');
  const [error, setError] = useState('');
  const [sentTo, setSentTo] = useState('');

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const address = email.trim().toLowerCase();
    if (!address || state === 'sending') return;
    setState('sending');
    setError('');
    try {
      const response = await fetch('/api/next-stay-offer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: address, name, status }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) {
        setSentTo(address);
        setState(data.alreadySent ? 'already' : 'sent');
      } else {
        setState('error');
        setError(data.error || 'We couldn’t send it. Please try again.');
      }
    } catch {
      setState('error');
      setError('Network error. Please try again.');
    }
  };

  return (
    <section className="status-offer" aria-labelledby="status-offer-title">
      <p className="status-offer-eyebrow">Coming back?</p>
      <h2 id="status-offer-title">15% off your next stay</h2>
      {state === 'sent' || state === 'already' ? (
        <p className="status-offer-done" role="status">
          <span aria-hidden="true">✓</span>
          <span className="status-offer-done-text">
            {state === 'sent' ? (
              <>Sent! Check <strong>{sentTo}</strong> for your 15% link.</>
            ) : (
              <>You&rsquo;re all set — we already sent your 15% link to <strong>{sentTo}</strong>.</>
            )}
          </span>
        </p>
      ) : (
        <form onSubmit={submit}>
          <p className="status-offer-hint" id="status-offer-hint">
            Leave your email and we&rsquo;ll send a direct-booking link. Just one email — no mailing list.
          </p>
          <label className="sr-only" htmlFor="status-offer-email">Email</label>
          <input
            id="status-offer-email"
            name="offerEmail"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            placeholder="you@example.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            aria-describedby="status-offer-hint"
            maxLength={GUEST_EMAIL_MAX_LENGTH}
            required
          />
          {state === 'error' && <p className="status-offer-error" role="alert">{error}</p>}
          <button type="submit" className="status-submit" disabled={state === 'sending'}>
            {state === 'sending' ? 'Sending…' : 'Send my 15% link'}
          </button>
        </form>
      )}
    </section>
  );
};

interface GuestStatusProps {
  onReturnHome?: () => void;
}

// "Postcard" status page: two postage stamps to pick from over a drawn skyline,
// one guestbook form (name, optional email to stay connected, mood) for both
// check-in and check-out, and a postcard postmarked today when done. No mailing list.
const GuestStatus: React.FC<GuestStatusProps> = ({
  onReturnHome = () => window.location.assign('/'),
}) => {
  const [step, setStep] = useState<Step>('choose');
  const [status, setStatus] = useState<Status | null>(null);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [mood, setMood] = useState<Mood>('great');
  const [confirmedName, setConfirmedName] = useState('');
  const [now, setNow] = useState(() => new Date());
  const submittingRef = useRef(false);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    ensureStatusDeviceCookie();
  }, []);

  const beginStatus = (newStatus: Status) => {
    const savedName = readGuestNameCookie();
    setGuestName(savedName === 'Guest' ? '' : savedName);
    setGuestEmail('');
    setMood('great');
    setStatus(newStatus);
    submittingRef.current = false;
    setStep('form');
  };

  const submitStatus = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!status || submittingRef.current) return;

    const formData = new FormData(event.currentTarget);
    const submittedName = String(formData.get('name') || '');
    const submittedEmail = String(formData.get('email') || '');
    if (!submittedName.trim()) return;

    const normalizedName = normalizeGuestName(submittedName);
    const normalizedEmail = submittedEmail.trim().toLowerCase().slice(0, GUEST_EMAIL_MAX_LENGTH);
    setGuestName(normalizedName);
    setGuestEmail(normalizedEmail);
    setConfirmedName(normalizedName === 'Guest' ? '' : normalizedName.split(' ')[0]);
    saveGuestNameCookie(normalizedName);
    submittingRef.current = true;
    void postGuestStatus({
      status,
      guestName: normalizedName,
      email: normalizedEmail,
      mood,
    }).catch(() => {
      // Keep the flow fast even if the background notification is unavailable.
    });
    setStep('confirmed');
  };

  const copy = status ? FORM_COPY[status] : null;

  return (
    <div className="status-page">

      <main className="status-inner">
        {step === 'choose' && (
          <section className="status-choose">
            <p className="status-eyebrow">Corazón Townhouse</p>
            <h1 className="status-title">
              You&apos;re <em>here.</em>
            </h1>
            <p className="status-time">{formatDate(now)}<b>{formatTime(now)}</b></p>
            <p className="status-lede">Let us know with one tap.</p>

            <div className="status-cards">
              <button
                type="button"
                aria-label="I've Arrived"
                className="status-card status-card--primary"
                onClick={() => beginStatus('arrived')}
              >
                <span className="status-card-face">
                  <span className="status-card-icon"><KeyIcon /></span>
                  <span className="status-card-text">
                    <strong>I&apos;ve arrived</strong>
                    <small>You made it in</small>
                  </span>
                </span>
              </button>
              <button
                type="button"
                aria-label="I've Departed"
                className="status-card"
                onClick={() => beginStatus('departed')}
              >
                <span className="status-card-face">
                  <span className="status-card-icon"><SuitcaseIcon /></span>
                  <span className="status-card-text">
                    <strong>I&apos;ve departed</strong>
                    <small>You&apos;ve headed out</small>
                  </span>
                </span>
              </button>
            </div>
            <Skyline />
          </section>
        )}

        {step === 'form' && status && copy && (
          <form className="status-form-wrap" onSubmit={submitStatus} autoComplete="on" data-status={status}>
            <span className="status-pin"><i aria-hidden="true" />{copy.pin}</span>
            <h1 className="status-title status-title--form">{copy.title}</h1>

            <div className="status-sheet">
              <p className="status-sheet-note" aria-hidden="true">Sign the guestbook</p>
              <div className="status-field">
                <label htmlFor="status-name">Name</label>
                <input
                  id="status-name"
                  name="name"
                  type="text"
                  value={guestName}
                  onChange={(event) => setGuestName(event.target.value)}
                  autoComplete="name"
                  autoFocus
                  enterKeyHint="next"
                  maxLength={GUEST_NAME_MAX_LENGTH}
                  required
                />
              </div>

              <div className="status-field">
                <label htmlFor="status-email">Email <span>(optional)</span></label>
                <input
                  id="status-email"
                  name="email"
                  type="email"
                  inputMode="email"
                  value={guestEmail}
                  onChange={(event) => setGuestEmail(event.target.value)}
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  enterKeyHint="next"
                  maxLength={GUEST_EMAIL_MAX_LENGTH}
                  aria-describedby="status-email-note"
                />
                <p id="status-email-note" className="status-field-note">
                  Leave it if you&rsquo;d like to stay connected. We won&rsquo;t add you to any mailing list.
                </p>
              </div>

              <fieldset className="status-moods">
                <legend>{copy.moodLegend}</legend>
                <div>
                  {CHECK_IN_MOOD_OPTIONS.map((option) => {
                    const selected = mood === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        aria-label={option.label}
                        aria-pressed={selected}
                        className={selected ? 'is-selected' : ''}
                        onClick={() => setMood(option.value)}
                      >
                        <MoodFace mood={option.value} />
                        <small>{option.label}</small>
                      </button>
                    );
                  })}
                </div>
              </fieldset>

              <button type="submit" className="status-submit">
                {copy.submit}
              </button>
              <button type="button" className="status-back" onClick={() => setStep('choose')}>
                Back
              </button>
            </div>
          </form>
        )}

        {step === 'confirmed' && status && (
          <section className="status-done" data-status={status}>
            <div className="status-postcard">
              <div className="status-postcard-message">
                <p className="status-eyebrow">
                  {status === 'arrived' ? 'Welcome' : 'Farewell'}{confirmedName ? `, ${confirmedName}` : ''}
                </p>
                <h1 className="status-title status-title--done">
                  {status === 'arrived' ? <>You&apos;re <em>checked in.</em></> : <>Thanks for <em>staying with us.</em></>}
                </h1>
                <p className="status-lede">
                  {status === 'arrived'
                    ? 'Wi-Fi, parking and the house notes are all in your guide.'
                    : 'Safe travels. We hope to welcome you back to Capitol Hill.'}
                </p>
              </div>
              <div className="status-postcard-address" aria-hidden="true">
                <span className="status-postcard-stamp">{status === 'arrived' ? <KeyIcon /> : <SuitcaseIcon />}</span>
                <span className="status-postmark">
                  <b>{formatPostmark(now)}</b>
                  <small>{status === 'arrived' ? 'Checked in' : 'Checked out'}</small>
                </span>
                <span className="status-postcard-line">To: {confirmedName || 'Guest'}</span>
                <span className="status-postcard-line">Capitol Hill</span>
              </div>
            </div>
            <NextStayOffer status={status} name={guestName} initialEmail={guestEmail} />
            <button type="button" className="status-ghost" onClick={onReturnHome}>
              {status === 'arrived' ? 'Open your guide' : 'Return to Homepage'}
            </button>
          </section>
        )}
      </main>
    </div>
  );
};

export default GuestStatus;
